import { useTranslation } from 'react-i18next';

import {
  type AccessGrant,
  type AccessRule,
  type BoardRuleInput,
  type MemberRole,
  RoleGrantsField,
  TeamGrantsField,
  useBoardRules,
  useDeleteBoardRule,
  useSetBoardRule,
} from '@/entities/collab';
import { FormError } from '@/shared/forms';

import styles from './BoardAccess.module.css';

const subjectId = (rule: AccessRule): string =>
  rule.subject_kind === 'team'
    ? (rule.team_id ?? '')
    : rule.subject_kind === 'custom_role'
      ? `custom:${rule.custom_role_id ?? ''}`
      : `role:${rule.subject_role ?? ''}`;

function inputFor(kind: 'team' | 'roles', grant: AccessGrant): BoardRuleInput {
  if (kind === 'team') {
    return { subject_kind: 'team', team_id: grant.id, granted_role: grant.role };
  }
  return grant.id.startsWith('custom:')
    ? { subject_kind: 'custom_role', custom_role_id: grant.id.slice(7), granted_role: grant.role }
    : { subject_kind: 'role', subject_role: grant.id.slice(5), granted_role: grant.role };
}

/**
 * Teams and workspace roles that have this board, with the role each one gets - the same two
 * fields as the "new board" form, but saving as you go: choosing, changing or removing one is one
 * request that the server checks again (never above the viewer's own authority). Rules that the
 * workspace administrator made by LABEL are listed in the workspace's rules, not here.
 */
export function GroupsTab({
  boardId,
  workspaceId,
  assignable,
}: {
  boardId: string;
  workspaceId: string;
  /** Roles the viewer may hand out. */
  assignable: readonly MemberRole[];
}) {
  const { t } = useTranslation();
  const rules = useBoardRules(boardId);
  const save = useSetBoardRule(boardId);
  const remove = useDeleteBoardRule(boardId);
  const busy = save.isPending || remove.isPending;
  const own = (rules.data ?? []).filter((rule) => rule.board_id === boardId);
  const grantsOf = (kind: 'team' | 'roles'): AccessGrant[] =>
    own
      .filter((rule) => (kind === 'team') === (rule.subject_kind === 'team'))
      .map((rule) => ({ id: subjectId(rule), role: rule.granted_role }));
  const idOf = (rule: AccessRule) => subjectId(rule);

  /** Bring the server in step with the field: removed -> delete, new or changed -> put. */
  const sync = (kind: 'team' | 'roles', next: AccessGrant[]) => {
    const before = grantsOf(kind);
    for (const gone of before.filter((g) => !next.some((n) => n.id === g.id))) {
      const rule = own.find((r) => idOf(r) === gone.id);
      if (rule) remove.mutate(rule.id);
    }
    for (const grant of next) {
      const was = before.find((g) => g.id === grant.id);
      if (!was || was.role !== grant.role) save.mutate(inputFor(kind, grant));
    }
  };

  return (
    <div className={styles.body}>
      <p className={styles.sub}>{t('access.groups.hint')}</p>
      <TeamGrantsField
        workspaceId={workspaceId}
        enabled
        value={grantsOf('team')}
        roles={assignable}
        disabled={busy || rules.isPending}
        onChange={(next) => {
          sync('team', next);
        }}
        texts={{
          title: t('access.groups.teams'),
          hint: t('access.groups.teamsHint'),
          add: t('boards.addTeamAccess'),
          remove: (name) => t('boards.removeGrant', { name }),
        }}
      />
      <RoleGrantsField
        workspaceId={workspaceId}
        enabled
        value={grantsOf('roles')}
        roles={assignable}
        disabled={busy || rules.isPending}
        onChange={(next) => {
          sync('roles', next);
        }}
        texts={{
          title: t('access.groups.roles'),
          hint: t('access.groups.rolesHint'),
          add: t('boards.addRoleAccess'),
          remove: (name) => t('boards.removeGrant', { name }),
        }}
      />
      <FormError error={save.error ?? remove.error} />
    </div>
  );
}
